// Interfaces para IdentityForm (Crear/Editar Intervinientes)

import type { IdentitiesContractNavigationState } from "@/screen/Identities/identitiesRoutes";

// Tipo de rol del interviniente
export type IdentityRole = "Owner" | "Guest";

// Modo del formulario
export type FormMode = "create" | "createMinor" | "edit";

// Estado de navegación para pasar datos sensibles sin exponerlos en la URL
// Cumple con OWASP: Broken Access Control - evita IDOR
export interface IdentityNavigationState extends IdentitiesContractNavigationState
{
    userIdentityId?: number; // Solo para modo edición, se pasa por estado de navegación
    preloadedIdentityDetail?: import("@/services/UserIdentitiesService").UserIdentityDetailDto;
}

// Dirección
export interface Address
{
    roadType?: string;
    streetName: string;
    streetNumber: string;
    streetFloor?: string;
    streetStair?: string;
    streetDoor?: string;
    streetOther?: string;
    zipCode: string;
    city: string;
    state: string;
    country: string;
}

// Fila de actividad económica por cuenta ajena. Los códigos (status, CNO) viajan
// como string del catálogo; null = campo de la fila sin rellenar todavía.
export interface KycEmployedActivityRow
{
    activityStatus: string | null;
    cnoCode: string | null;
    years: number | null;
    employedCurrentCompany: string | null;
}

// Fila de actividad económica por cuenta propia.
export interface KycSelfEmployedActivityRow
{
    activityStatus: string | null;
    cnaeCode: string | null;
    years: number | null;
    relatedProfessionalActivity: string | null;
}

// Datos del interviniente
export interface IdentityFormData
{
    id?: number;
    firstName: string;
    lastName: string;
    secondLastName: string;
    email: string;
    countryPrefix: string;
    phone: string;
    operationCountryPrefix: string;
    operationPhone: string;
    signatureCountryPrefix: string;
    signaturePhone: string;
    useSinglePhone: boolean;
    birthday: Date | null;
    birthdayCountry: string;
    nationalCountry: string;
    idType: "DNI" | "NIE" | "";
    idDocument: string;
    idExpiration: Date | null;
    /** Validez permanente del documento. El backend es el dueño de la regla: solo se envía el flag,
     *  nunca la fecha centinela con la que él la representa en BD. */
    hasPermanentIdExpiration: boolean;
    idFront: File | string | null;
    idBack: File | string | null;
    postalAddress: Address | null;
    fiscalAddress: Address | null;
    sameAddress: boolean;
    role: IdentityRole;
    main: boolean;
    isMinor: boolean;
    economicActivity?: string | null;
    proofOfIncome?: File | string | null;
    gafiQuestion?: boolean;
    publicPosition?: boolean;
    maxContribution?: number | null;
    proofOfMinorGuardian?: File | string | null;
    proofOfMinorGuardianDescription?: string;
    maritalStatus?: number | null;
    profession?: string | null;
    residenceCountry?: string | null;
    fatcaStatus?: number | null;
    residenceOfficialDocument?: number | null;
    noResidenceDocument?: number | null;
    hasTaxIdInUS?: boolean;
    hasResidenceInUS?: boolean;
    hasTaxObligationInUS?: boolean;
    // Los enums viajan como código string del catálogo; boolean sin contestar = undefined.
    annualGrossIncome: string | null;
    hasEmployedActivity?: boolean;
    hasSelfEmployedActivity?: boolean;
    employedActivities: KycEmployedActivityRow[];
    selfEmployedActivities: KycSelfEmployedActivityRow[];
    fundsOrigin: string | null;
    fundsDocumentationProvided: string | null;
    internationalCommercialActivity?: boolean;
    isPepRelative?: boolean;
    numberOfDependants: number | null;
    otherNationalities?: boolean;
    otherNationality: string;
    otherPersonalCircumstances: string | null;
    riskCountries?: boolean;
    riskJurisdictionActivity?: boolean;
    maritalSituation: string | null;
    isUsCitizen?: boolean;
}

// Estado de validación del formulario
export interface FormValidation
{
    firstName: boolean;
    lastName: boolean;
    secondLastName: boolean;
    email: boolean;
    phone: boolean;
    operationPhone: boolean;
    signaturePhone: boolean;
    countryPrefix: boolean;
    operationCountryPrefix: boolean;
    signatureCountryPrefix: boolean;
    birthday: boolean;
    birthdayCountry: boolean;
    nationalCountry: boolean;
    idDocument: boolean;
    idExpiration: boolean;
    idFront: boolean;
    idBack: boolean;
    postalAddress: boolean;
    fiscalAddress: boolean;
    economicActivity: boolean;
    proofOfIncome: boolean;
    gafiQuestion: boolean;
    publicPosition: boolean;
    proofOfMinorGuardian: boolean;
    // Datos Fiscales
    maritalStatus: boolean;
    profession: boolean;
    residenceCountry: boolean;
    fatcaStatus: boolean;
    residenceOfficialDocument: boolean;
    noResidenceDocument: boolean;
    hasTaxIdInUS: boolean;
    hasResidenceInUS: boolean;
    hasTaxObligationInUS: boolean;
    annualGrossIncome: boolean;
    hasEmployedActivity: boolean;
    hasSelfEmployedActivity: boolean;
    // true cuando la puerta está contestada y sus filas (si las hay) están completas
    employedActivities: boolean;
    selfEmployedActivities: boolean;
    fundsOrigin: boolean;
    fundsDocumentationProvided: boolean;
    internationalCommercialActivity: boolean;
    isPepRelative: boolean;
    numberOfDependants: boolean;
    otherNationalities: boolean;
    // Condicional: solo exigible cuando otherNationalities === true
    otherNationality: boolean;
    otherPersonalCircumstances: boolean;
    riskCountries: boolean;
    riskJurisdictionActivity: boolean;
    // Condicional: solo exigible cuando el estado civil es Casado
    maritalSituation: boolean;
    isUsCitizen: boolean;
}

// Opciones para selectores
export interface SelectOption
{
    key: string;
    text: string;
}

export interface CountryCatalogItem
{
    key: string;
    text: string;
    value: string;
}

// Respuesta del backend al guardar
export interface SaveIdentityResponse
{
    success: boolean;
    message: string;
    identityId?: number;
}

// Error de validación
export interface ValidationError
{
    field: string;
    message: string;
}

// Error tipado que produce identityAttachmentsService al fallar la descarga de un adjunto
export interface AttachmentLoadError
{
    readonly kind: "AttachmentLoadError";
    readonly fileName: string;
    readonly httpStatus: number | null;
    readonly message: string;
}

export function makeAttachmentLoadError(fileName: string, httpStatus: number | null): AttachmentLoadError
{
    return { kind: "AttachmentLoadError", fileName, httpStatus, message: `Error al descargar el adjunto "${fileName}".` };
}

export function isAttachmentLoadError(value: unknown): value is AttachmentLoadError
{
    return typeof value === "object" && value !== null && (value as AttachmentLoadError).kind === "AttachmentLoadError";
}

// Adjunto remoto resuelto a blob (una sola descarga, reutilizable para preview y cálculo)
export interface ResolvedAttachment
{
    blob: Blob;
    objectUrl: string;
    mimeType: string;
    fileName: string;
}

// Resultado del procesamiento del DNI
export interface DocumentProcessingResult
{
    success: boolean;
    firstName?: string;
    lastName?: string;
    secondLastName?: string;
    documentNumber?: string;
    dateOfBirth?: string;
    dateOfExpiration?: string;
    /** El análisis ha leído "PERMANENTE" en la validez del documento (DNI de mayores de 70 años). */
    hasPermanentIdExpiration?: boolean;
    /** Nacionalidad leída (código ISO alfa-3, p. ej. "ESP"). */
    nationality?: string;
    /** Domicilio leído (en el DNI, sin código postal). */
    address?: import("@/services/UserIdentitiesService").DocumentAddressDto;
    documentType?: string;
    idFrontFile?: File;
    idBackFile?: File;
    idFrontPreview?: string;
    idBackPreview?: string;
    error?: string;
}

export interface IdentityErrorHelperProps
{
    errors: ValidationError[];
    errorsRef?: React.RefObject<HTMLDivElement>;
}

// Props del componente de subida de documento
export interface IdentityDocumentUploadProps
{
    mode: FormMode;
    userIdentityId?: number;
    initialFrontPreview?: string | null;
    initialBackPreview?: string | null;
    /** DNI/NIE ya persistido en el interviniente (vacío en borradores guardados sin integración). */
    initialIdDocument?: string;
    /** Permite sustituir los archivos del documento de identidad (sin análisis automático) para renovar
     *  un documento caducado. Aplica en edición a intervinientes ACTIVE, OUTDATED y PENDING, y a los
     *  que llegan sin alguna de las dos imágenes. */
    canRenewIdentityDocument?: boolean;
    /** Al interviniente le falta el anverso, el reverso o ambos. */
    isMissingIdentityDocuments?: boolean;
    /** La fecha de validez persistida está caducada. Distingue el texto de la renovación: sin ella, un
     *  documento vigente en un estado renovable se anunciaba como caducado. */
    isStoredDocumentExpired?: boolean;
    /** No hay fecha de validez persistida: el texto de la renovación debe pedir que se rellene, porque el
     *  formulario no deja guardar sin ella. */
    isStoredIdExpirationMissing?: boolean;
    /** El interviniente tiene anverso y reverso persistidos en BD, aunque no se hayan podido
     *  recuperar las previews. Permite pedir el DNI manualmente en lugar de dejar el formulario bloqueado. */
    hasStoredDocuments?: boolean;
    onDocumentProcessed: (result: DocumentProcessingResult, frontPreview: string, backPreview: string) => void;
    onServerWarning: (message: string) => void;
    onDniConfirmed: (dni: string) => void;
    onError: (error: string) => void;
    onAttachmentErrors: (errors: ValidationError[]) => void;
    onPreviewLoadingChange: (loading: boolean) => void;
    /** Llamado cuando el ciclo upload→analyzeStatus se completa con éxito (phase = flow_resolved).
     *  Los errores no llegan aquí: la fase vuelve a idle/dni_required para que el usuario reintente. */
    onFlowResolved: (action: import("@/services/UserIdentitiesService").UserIdentityFlowAction | null, status: import("@/services/UserIdentitiesService").UserIdentityStatusDto | null) => void;
}